// src/pages/ChannelSubscriptionDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BlockIcon from '@mui/icons-material/Block';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface SubscriptionDetail {
  task_id: string;
  user_id: string;
  status: string;
  accounts_count: number;
  channels_count: number;
  started_at?: string;
  stopped_at?: string;
  completed_at?: string;
  paused_at?: string | null;
  duration?: number;

  total_subscribed: number;
  total_failed: number;
  total_already_subscribed: number;
  total_skipped: number;
  total_channels: number;
  progress_percentage: number;

  accounts_used: number;
  accounts_banned: number;
  accounts_with_flood: number;

  subscriptions_per_account: Record<string, number>;
  error_breakdown: Record<string, number>;
  flood_waits: Array<{ account: string; channel: string; wait_time: number }>;

  average_speed?: number;
  estimated_completion?: string;
  status_message?: string;

  subscription_params: Record<string, any>;
}

const ChannelSubscriptionDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      navigate('/channel_subscription');
      return;
    }
  }, [jobId, navigate]);

  const fetchDetail = async () => {
    if (!jobId) return;

    try {
      const res = await api.get(`/subscribe_to_channels/status/${jobId}`);
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Задача не найдена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchDetail();
      const interval = setInterval(fetchDetail, 7000);
      return () => clearInterval(interval);
    }
  }, [jobId]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка автоподписки...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Задача не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/channel_subscription')}>
          Назад к автоподпискам
        </Button>
      </Container>
    );
  }

  const uptime = data.duration ? Math.floor(data.duration) : 0;
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  const p = data.subscription_params;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/channel_subscription')} sx={{ mb: 4 }}>
        Назад к автоподпискам
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Автоподписка #{jobId.slice(0, 8)}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          <AccountCircleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Аккаунтов: <strong>{data.accounts_count}</strong> · Каналов: <strong>{data.channels_count}</strong> ·
          <AccessTimeIcon sx={{ verticalAlign: 'middle', mx: 1 }} />
          Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
        </Typography>

        {/* Основные карточки */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {data.total_subscribed}
                </Typography>
                <Typography color="text.secondary">Подписано</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {data.total_already_subscribed}
                </Typography>
                <Typography color="text.secondary">Уже подписаны</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {data.total_skipped}
                </Typography>
                <Typography color="text.secondary">Пропущено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {data.total_failed}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Дополнительные показатели */}
        <Grid container spacing={3} mb={6}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} mb={1}>
                <BlockIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Забанено
              </Typography>
              <Typography variant="h4" color="#DC2626">{data.accounts_banned}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} mb={1}>
                FloodWait
              </Typography>
              <Typography variant="h4" color="#F59E0B">{data.accounts_with_flood}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} mb={1}>
                Прогресс
              </Typography>
              <Typography variant="h4" color="#3B82F6">{data.progress_percentage.toFixed(0)}%</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Скорость */}
        {data.average_speed && (
          <Box sx={{ p: 4, bgcolor: '#EFF6FF', borderRadius: 2, mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Скорость и прогноз
            </Typography>
            <Typography sx={{ fontSize: '1.1rem' }}>
              <SpeedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Скорость: <strong>{data.average_speed.toFixed(1)} подписок/час</strong>
              {data.estimated_completion && (
                <> · Завершится примерно: <strong>{new Date(data.estimated_completion).toLocaleString('ru-RU')}</strong></>
              )}
            </Typography>
          </Box>
        )}

        {/* Статусное сообщение */}
        {data.status_message && (
          <Alert severity={data.status === 'completed' ? 'success' : data.status === 'running' ? 'info' : 'warning'} sx={{ mb: 4 }}>
            {data.status_message}
          </Alert>
        )}

        {/* Параметры запуска */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
            Параметры запуска
          </Typography>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell><strong>Дневной лимит</strong></TableCell><TableCell>{p.daily_limit_per_account || 'Без лимита'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Общий лимит</strong></TableCell><TableCell>{p.total_subscriptions_limit || 'Без лимита'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Лимит на аккаунт</strong></TableCell><TableCell>{p.subscriptions_per_account || 'Без лимита'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Временной лимит</strong></TableCell><TableCell>{p.time_limit_hours ? `${p.time_limit_hours} ч` : 'Без лимита'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Задержка между подписками</strong></TableCell><TableCell>{p.min_delay}–{p.max_delay} сек</TableCell></TableRow>
                <TableRow><TableCell><strong>Подписок в час</strong></TableCell><TableCell>{p.subscriptions_per_hour || 'Без лимита'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Пауза каждый час</strong></TableCell><TableCell>{p.hourly_pause ? `${p.hourly_pause} сек` : 'Без пауз'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Естественное поведение</strong></TableCell><TableCell>{p.natural_behavior ? 'Включено' : 'Выключено'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Чтение описания</strong></TableCell><TableCell>{p.read_description_delay_min}–{p.read_description_delay_max} сек</TableCell></TableRow>
                <TableRow><TableCell><strong>Пропуск при FloodWait</strong></TableCell><TableCell>{p.skip_on_long_flood ? 'Да' : 'Нет'}</TableCell></TableRow>
                <TableRow><TableCell><strong>Стратегия распределения</strong></TableCell>
                  <TableCell>
                    {p.distribution_strategy === 'equal' && 'Равномерно'}
                    {p.distribution_strategy === 'random' && 'Случайно'}
                    {p.distribution_strategy === 'sequential' && 'Последовательно'}
                  </TableCell>
                </TableRow>
                <TableRow><TableCell><strong>Параллельные аккаунты</strong></TableCell><TableCell>{p.use_parallel_accounts ? `Да (${p.max_parallel_accounts})` : 'Нет'}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Ошибки */}
        {Object.keys(data.error_breakdown || {}).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#DC2626" fontWeight={600} gutterBottom>
              Ошибки по типам
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(data.error_breakdown).map(([type, count]) => (
                <Chip
                  key={type}
                  icon={<ErrorIcon />}
                  label={`${type}: ${count}`}
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* FloodWait */}
        {data.flood_waits.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#F59E0B" fontWeight={600} gutterBottom>
              FloodWait (ожидание)
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Аккаунт</strong></TableCell>
                    <TableCell><strong>Канал</strong></TableCell>
                    <TableCell align="right"><strong>Ожидание (сек)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.flood_waits.map((w, i) => (
                    <TableRow key={i}>
                      <TableCell>{w.account}</TableCell>
                      <TableCell>{w.channel}</TableCell>
                      <TableCell align="right">{w.wait_time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Время */}
        <Box sx={{ p: 4, bgcolor: '#F9FAFB', borderRadius: 2 }}>
          <Typography variant="h6" color="#1E40AF" gutterBottom>
            Время
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Запущено</Typography>
              <Typography fontWeight={600}>{new Date(data.started_at || '').toLocaleString('ru-RU')}</Typography>
            </Grid>
            {data.stopped_at && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Остановлено</Typography>
                <Typography fontWeight={600} color="#F59E0B">
                  {new Date(data.stopped_at).toLocaleString('ru-RU')}
                </Typography>
              </Grid>
            )}
            {data.completed_at && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Завершено</Typography>
                <Typography fontWeight={600} color="#10B981">
                  {new Date(data.completed_at).toLocaleString('ru-RU')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default ChannelSubscriptionDetail;